import { computed, ComputedRef, onMounted, Ref, ref, watch } from 'vue';
import {
  ConfigurableProductSizeObject,
  ConfigurableProductSizeValueObject,
} from '@/types/joostconnector';
import { SelectorOption } from '@/types/Configurator/SelectorOption';
import _ from 'lodash';
import { useConfiguratorStore } from './ConfiguratorStore';
import { promiseTimeout, watchDeep } from '@vueuse/core';
import { MediaclipProductType } from '@/types/Mediaclip/MediaclipProductType';
import { defineStore } from 'pinia';
import { useFilterStore } from './FilterStore';
import type { ImageDimensions } from '@/types/Configurator/ImageDimensions';
import { useExtraOptionsStore } from '../ExtraOptionsStore';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication';
import { useMediaclipStore } from './MediaclipStore';
import { useExtraOptions } from '@/composables/UseExtraOptions';
import type { PaazlDeliveryOptionsRequestObject } from '@/types/Paazl/PaazlDeliveryOptionsRequestObject.ts';

export const useSizeStore = defineStore(
  `size-store-${window.product.sku}`,
  () => {
    const usePreferredSize = [
      MediaclipProductType.CONFIGURATOR.valueOf(),
    ].includes(
      Number(useConfiguratorStore().product?.mediaclip_product_type.valueOf())
    );

    const configuratorStore = useConfiguratorStore();
    const filterStore = useFilterStore();
    const extraOptions = useExtraOptions();
    const extraOptionsStore = useExtraOptionsStore();
    const mediaclipStore = useMediaclipStore();
    const mediaclipCommunicationComposable = useMediaclipCommunication();

    /**
     * Map size value object to SelectorOption
     * @param size
     */
    function sizeValueToDropdown(
      size: ConfigurableProductSizeValueObject
    ): SelectorOption {
      return {
        label: size.label,
        value: size.attributes.sku,
        price: size.attributes.price ?? undefined,
        localizedPrice: size.attributes.localizedPrice ?? undefined,
        recommendedPrice: size.attributes.recommendedPrice ?? undefined,
        localizedRecommendedPrice:
          size.attributes.localizedRecommendedPrice ?? undefined,
      };
    }

    /**
     * Filter size values by selected filters, and sort by width and height
     * @param sizes
     */
    function filterSizeValues(
      sizes: Record<string, ConfigurableProductSizeValueObject>
    ): Record<string, ConfigurableProductSizeValueObject> {
      return _(sizes)
        .filter(sizeValue =>
          _.every(
            filterStore.filters,
            (_, key: string) =>
              sizeValue.attributes[key] ===
              filterStore.selectedFilterValues[key].value
          )
        )
        .sortBy(['attributes.width', 'attributes.height'])
        .keyBy('attributes.sku')
        .value();
    }

    const paazlDeliveryOptionsRequestObject: ComputedRef<PaazlDeliveryOptionsRequestObject> =
      computed(() => ({
        sku: selectedSizeValue.value.attributes.sku!,
        shipping_cutoff_time:
          selectedSizeValue.value.attributes.shippingCutoffTime ?? undefined,
        processing_days: Number(
          selectedSizeValue.value.attributes.processingDays
        ),
        width: Number(selectedSizeValue.value.attributes.width),
        height: Number(selectedSizeValue.value.attributes.height),
        paazl_delivery_matrix:
          selectedSizeValue.value.attributes.paazlDeliveryMatrix ?? undefined,
        product_price: Number(selectedSizeValue.value.attributes.price),
      }));

    const sizes: Ref<ConfigurableProductSizeObject> = ref(
      window.configurable_product!.sizes!.sizes
    );
    const preferredSizes: Ref<ConfigurableProductSizeObject> = ref(
      window.configurable_product!.sizes!.preferredSizes
    );

    const filteredSizeValues: ComputedRef<
      Record<string, ConfigurableProductSizeValueObject>
    > = computed(() =>
      filterSizeValues(
        sizes.value.values as unknown as Record<
          string,
          ConfigurableProductSizeValueObject
        >
      )
    );
    const filteredPreferredSizeValues: ComputedRef<Record<
      string,
      ConfigurableProductSizeValueObject
    > | null> = computed(() =>
      Object.values(preferredSizes.value.values).length > 1
        ? filterSizeValues(
            preferredSizes.value.values as unknown as Record<
              string,
              ConfigurableProductSizeValueObject
            >
          )
        : null
    );

    // Filter out the sizes which don't have a price
    const sizeOptions: ComputedRef<SelectorOption[]> = computed(
      () =>
        _.filter(
          _.filter(_.map(filteredSizeValues.value, sizeValueToDropdown)),
          (value: SelectorOption) => value.price
        ) as SelectorOption[]
    );

    const preferredSizeOptions: ComputedRef<SelectorOption[]> = computed(
      () =>
        _.filter(
          _.map(filteredPreferredSizeValues.value, sizeValueToDropdown)
        ) as SelectorOption[]
    );

    const allAvailableSizeValues: ComputedRef<
      Record<string, ConfigurableProductSizeValueObject>
    > = computed(() => ({
      ...(_.keyBy(
        preferredSizes.value.values,
        (size: ConfigurableProductSizeValueObject) => size.attributes.sku
      ) as Record<string, ConfigurableProductSizeValueObject>),
      ...(_.keyBy(
        sizes.value.values,
        (size: ConfigurableProductSizeValueObject) => size.attributes.sku
      ) as Record<string, ConfigurableProductSizeValueObject>),
    }));

    const filteredAvailableSizeValues: ComputedRef<
      Record<string, ConfigurableProductSizeValueObject>
    > = computed(() => filterSizeValues(allAvailableSizeValues.value));

    const hasMoreThanOneFilteredSize: ComputedRef<boolean> = computed(
      () => Object.keys(filteredAvailableSizeValues.value).length > 1
    );

    const filteredAvailableSizeOptions: ComputedRef<
      Record<string, SelectorOption>
    > = computed(
      () =>
        _.filter(
          _.filter(
            _.map(filteredAvailableSizeValues.value, sizeValueToDropdown)
          ),
          value => value.price
        ) as unknown as Record<string, SelectorOption>
    );

    function getDefaultSku(): string | null {
      return usePreferredSize
        ? preferredSizes.value.default
        : sizes.value.default;
    }

    function getCheapestProduct(
      products: Record<string, ConfigurableProductSizeValueObject>
    ): SelectorOption {
      return sizeValueToDropdown(
        _.minBy(
          _.map(products, value => value),
          value => value?.attributes.price
        ) ?? getDefaultSizeValue()
      );
    }

    const mostChosenSku: ComputedRef<string> = computed(() => {
      // Get a filter which has default sku (at this time it's possible to only add 1 default product filter to a configurable product)
      const selectedFilter = _.find(
        filterStore.selectedFilterValues,
        filter => !_.isNil(filter.defaultSku)
      );

      // Get the default size for filter
      const defaultSizeForFilter = _.find(
        allAvailableSizeValues.value,
        (sizes: ConfigurableProductSizeValueObject) =>
          sizes.attributes.sku === selectedFilter?.defaultSku
      ) as ConfigurableProductSizeValueObject;

      // Get default sku which has the same width and height as the default filter sku
      const defaultSkuForSize = _.find(
        filteredPreferredSizeValues.value,
        (size: ConfigurableProductSizeValueObject) =>
          size.attributes.width === defaultSizeForFilter?.attributes.width &&
          size.attributes.height === defaultSizeForFilter?.attributes.height
      ) as ConfigurableProductSizeValueObject;

      // Return the right sku
      return (
        defaultSkuForSize?.attributes.sku ??
        selectedFilter?.defaultSku ??
        getDefaultSku()
      );
    });

    /**
     * Get a default value object for a given size object
     */
    function getDefaultSizeValue(): ConfigurableProductSizeValueObject {
      const filteredValues = filteredAvailableSizeValues.value;
      return (
        filteredValues[mostChosenSku.value ?? Object.keys(filteredValues)[0]] ??
        filteredValues[Object.keys(filteredValues)[0]]
      );
    }

    const selectedSizeOption: Ref<SelectorOption> = ref(
      sizeValueToDropdown(getDefaultSizeValue())
    );

    const selectedSizeValue: ComputedRef<ConfigurableProductSizeValueObject> =
      computed(
        () => allAvailableSizeValues.value[selectedSizeOption.value.value]
      );

    const previousSelectedSizeOption: Ref<SelectorOption> = ref(
      sizeValueToDropdown(getDefaultSizeValue())
    );

    const previousSelectedSizeValue: ComputedRef<ConfigurableProductSizeValueObject> =
      computed(
        () =>
          allAvailableSizeValues.value[previousSelectedSizeOption.value.value]
      );

    const imageDimensions: Ref<ImageDimensions> = ref({
      width: undefined,
      height: undefined,
    });

    const showMostChosenBadge: Ref<boolean> = ref(true);

    function getPreferredSizeBySku(
      sku: string
    ): ConfigurableProductSizeValueObject {
      return _.find(
        filteredPreferredSizeValues.value,
        (size: ConfigurableProductSizeValueObject) =>
          size.attributes.sku === sku
      ) as ConfigurableProductSizeValueObject;
    }

    async function handleMostChosenBadgeVisibility(
      hasTimeout: boolean = true
    ): Promise<void> {
      const mostChosenSize = getPreferredSizeBySku(mostChosenSku.value);

      // Wait 300 ms to avoid becoming visible to soon when size selector is closed (this looks a bit glitchy)
      if (hasTimeout) {
        await promiseTimeout(300);
      }

      showMostChosenBadge.value = !(
        selectedSizeValue.value.attributes.width >
          mostChosenSize.attributes.width &&
        selectedSizeValue.value.attributes.height >
          mostChosenSize.attributes.height
      );
    }

    watchDeep(
      () => selectedSizeOption.value,
      (value: SelectorOption) => {
        extraOptions
          .requestCategories(value.value as string)
          .then(extraOptionsStore.selectDefaultProductOptions);
      }
    );

    function getCurrentSelectedSize():
      | ConfigurableProductSizeValueObject
      | undefined {
      return _.find(
        filteredAvailableSizeValues.value,
        (size: ConfigurableProductSizeValueObject) =>
          size.attributes.sku === selectedSizeValue.value.attributes.sku
      );
    }

    watchDeep(
      () => filterStore.selectedFilterOptions,
      async (_, oldOption) => {
        if (!oldOption) {
          return;
        }

        const matchedSize = matchSizeOnDimensions(
          Object.values(
            filteredPreferredSizeValues.value ?? filteredSizeValues.value
          ),
          selectedSizeValue.value
        );

        selectedSizeOption.value = sizeValueToDropdown(
          getCurrentSelectedSize() ?? matchedSize ?? getDefaultSizeValue()
        );

        previousSelectedSizeOption.value = mostChosenSku.value
          ? getPreferredSizeBySku(mostChosenSku.value)
            ? sizeValueToDropdown(getPreferredSizeBySku(mostChosenSku.value))
            : selectedSizeOption.value
          : selectedSizeOption.value;

        if (
          !configuratorStore.isSimpleConfigurator ||
          configuratorStore.currentStep !== 0 ||
          !configuratorStore.mediaclipProjectId
        ) {
          return;
        }

        if (configuratorStore.allPicturesUploaded) {
          await mediaclipCommunicationComposable.updateProject();
          configuratorStore.isUpdatingProject = false;
          return;
        }

        if (!configuratorStore.isEditing) {
          // Destroy mediaclip hub and reset some booleans
          window.mediaclip.hub.destroy(true);
          configuratorStore.mediaclipInitialized = false;
          configuratorStore.mediaclipLaunched = false;
          configuratorStore.mediaclipProjectId = undefined;
          configuratorStore.mediaclipToken = undefined;
          configuratorStore.identifier = undefined;
          mediaclipStore.$reset();
          localStorage.removeItem(mediaclipStore.$id);
        }
      }
    );

    function matchSizeOnDimensions(
      sizes: ConfigurableProductSizeValueObject[],
      currentSize: ConfigurableProductSizeValueObject
    ) {
      if (!configuratorStore.hasSavedAtLeastOneTime) {
        return;
      }

      // Sort sizes by their absolute difference in width and height compared to the selected size.
      return _.sortBy(sizes, [
        size => Math.abs(currentSize?.attributes.width - size.attributes.width),
        size =>
          Math.abs(currentSize?.attributes.height - size.attributes.height),
      ])[0];
    }

    // Don't mount if testing
    if (!import.meta.env.VITEST) {
      onMounted(() =>
        filterStore.setFiltersToProductAttributes(getDefaultSizeValue())
      );
    }

    const formatPrice = (price: number) => window.hyva.formatPrice(price);
    const totalPrice: ComputedRef<number> = computed(
      () =>
        _.sumBy(
          Object.values(useExtraOptionsStore().selectedOptions),
          'price'
        ) + selectedSizeValue.value.attributes.price
    );
    const totalDisplayPrice: ComputedRef<string> = computed(() =>
      formatPrice(totalPrice.value / 100)
    );
    const totalRecommendedPrice: ComputedRef<number> = computed(
      () =>
        _.sumBy(
          Object.values(useExtraOptionsStore().selectedOptions),
          'recommendedPrice'
        ) + selectedSizeValue.value.attributes.recommendedPrice
    );
    const totalDisplayRecommendedPrice: ComputedRef<string> = computed(() =>
      formatPrice(totalRecommendedPrice.value / 100)
    );

    watch(
      () => selectedSizeValue.value,
      () => {
        mediaclipCommunicationComposable.updateProjectOnDesktop();
      }
    );

    return {
      sizes,
      filteredSizeValues,
      filteredAvailableSizeValues,
      sizeOptions,
      preferredSizes,
      preferredSizeOptions,
      filteredPreferredSizeValues,
      filteredAvailableSizeOptions,
      hasMoreThanOneFilteredSize,
      selectedSizeOption,
      allAvailableSizeValues,
      selectedSizeValue,
      getCheapestProduct,
      totalPrice,
      totalRecommendedPrice,
      totalDisplayPrice,
      totalDisplayRecommendedPrice,
      mostChosenSku,
      imageDimensions,
      previousSelectedSizeOption,
      previousSelectedSizeValue,
      showMostChosenBadge,
      handleMostChosenBadgeVisibility,
      paazlDeliveryOptionsRequestObject,
      getDefaultSku,
    };
  },
  {
    persist: {
      paths: [
        'selectedSizeOption',
        'previousSelectedSizeOption',
        'imageDimensions',
      ],
    },
  }
);
